import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService, { HomePageData } from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";

const FatQualityDetail = () => {
  const navigate = useNavigate();

  // Use React Query to fetch and cache home page data
  const { data: homeData, isLoading: loading } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
  });

  // Get fat quality data from API
  const insightData = homeData?.["Health Insights"]?.bad_fat_ratio_daily;
  const satTransRatio = insightData?.value ?? 0;
  const fatQualityStatus = insightData?.label ?? "Loading...";

  // Get actual gram values from Today's Nutrition
  const totalFat = homeData?.["Today's Nutrition"]?.["Total Fats"] || 0;
  const saturatedFat = homeData?.["Today's Nutrition"]?.["Total Saturated Fat"] || 0;
  const transFat = homeData?.["Today's Nutrition"]?.["Total Trans Fat"] || 0;

  // Get status color
  const getStatusColor = (status: string) => {
    if (status === "Excellent" || status === "Good") {
      return "text-green-600";
    }
    if (status === "Fair") {
      return "text-yellow-600";
    }
    if (status === "Needs Improvement" || status === "Poor") {
      return "text-red-600";
    }
    return "text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Fat Quality</h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-blue-50 to-blue-100 p-6 shadow-sm dark:from-blue-900/20 dark:to-blue-800/20">
          <div className="text-center">
            <p className="mb-2 text-sm text-foreground/70">
              Today's Fat Quality
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {satTransRatio}
            </div>
            <p
              className={`text-sm font-semibold ${getStatusColor(fatQualityStatus)}`}
            >
              {fatQualityStatus}
            </p>

            {/* Fat Breakdown */}
            <div className="mt-6 flex justify-center gap-8">
              <div className="text-center">
                <div className="text-xl font-bold text-foreground">
                  {Math.round(totalFat)}g
                </div>
                <div className="text-xs text-muted-foreground">Total Fat</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                  {Math.round(saturatedFat)}g
                </div>
                <div className="text-xs text-muted-foreground">Saturated</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-red-600 dark:text-red-400">
                  {Math.round(transFat)}g
                </div>
                <div className="text-xs text-muted-foreground">Trans Fat</div>
              </div>
            </div>
          </div>
        </Card>

        {/* What is Fat Quality */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Fat Quality?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Fat Quality measures the ratio of healthy (unsaturated) fats to
            unhealthy (saturated and trans) fats in your diet. It is calculated
            using your total fat, saturated fat, and trans fat intake. A higher
            score indicates better fat quality, which supports heart health and
            reduces inflammation.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">≤ 0.15</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">0.15 - 0.30</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">Above 0.30</span>
            </div>
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Tips to Improve Fat Quality
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Replace butter with olive oil or avocado oil</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Avoid processed and fried foods high in trans fats</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Choose lean proteins and remove visible fat from meats</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Add nuts, seeds, and fatty fish to your diet</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default FatQualityDetail;
